import { AbstractViewModel } from '../../../utils/viewmodels/viewmodel';
import { useViewModel } from '../../../utils/viewmodels/useViewModel';
import type { ChatSingleSelectContent } from '../../chatbot.types';
import type { ChatSingleSelectState } from './chat-single-select.state';

export class ChatSingleSelectViewModel extends AbstractViewModel<ChatSingleSelectState> {
  static initialState: ChatSingleSelectState = {
    prompt: '',
    options: [],
    selectedId: undefined,
    isAnswered: false,
  };

  private readonly messageId: string;
  private readonly onSelect: (messageId: string, optionId: string) => void;

  constructor(
    messageId: string,
    content: ChatSingleSelectContent,
    onSelect: (messageId: string, optionId: string) => void
  ) {
    super(ChatSingleSelectViewModel.initialState);
    this.messageId = messageId;
    this.onSelect = onSelect;

    this.setContent(content);
  }

  setContent = (content: ChatSingleSelectContent): void => {
    this.update((state) => {
      state.prompt = content.prompt;
      state.options = content.options;
      state.selectedId = content.selectedId;
      state.isAnswered = content.selectedId !== undefined;
    });
  };

  /** Whether a specific option is the selected one. */
  isSelected = (optionId: string): boolean => this.state.getValue().selectedId === optionId;

  /** Returns the CSS class for an option button. */
  getOptionClassName = (optionId: string): string =>
    `chatbot-option-btn ${this.isSelected(optionId) ? 'chatbot-option-btn--selected' : ''}`;

  /** Handle a user selection. */
  handleSelect = (optionId: string): void => {
    this.onSelect(this.messageId, optionId);
  };
}

export const useChatSingleSelectViewModel = (
  messageId: string,
  content: ChatSingleSelectContent,
  onSelect: (messageId: string, optionId: string) => void
) =>
  useViewModel<ChatSingleSelectState, ChatSingleSelectViewModel>(
    () => new ChatSingleSelectViewModel(messageId, content, onSelect)
  );
